import React, { useEffect } from 'react';
import { useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CustomDatePicker from 'common-ui/customDatePicker';
import MobileList from 'common-ui/mobileList';
import { AsyncSelect } from 'common-ui/select';
import { Table } from 'common-ui/table';
import { TICKET_STATUS_OPTIONS } from 'constants/constants';
import dayjs from 'dayjs';
import { getCustomersTicket } from 'features/customers/customersActions';
import {
  selectCustomerTicketLength,
  selectCustomerTicketLoading,
  selectCustomerTickets,
} from 'features/customers/customersSlice';
import AvatarGroup from 'pages/components/avatarGroup';
import Tag from 'pages/components/tag';
import { formatDateTime } from 'utils/dateUtils';
import { date } from 'yup';

import {
  ExpandableWrapper,
  ExpandedLabel,
  ExpandedValue,
  Row,
} from '../../../userManagement/UserManagement.styles';
import {
  ClearAllText,
  ClickableTicketId,
  CreatedDateBadge,
  CustomDatePickerWrapper,
  FiltersWrapper,
  FiltersWrapperDiv,
  Icon,
  Line,
  UpdatedDateBadge,
} from './Components.styles';

const TicketHistory = ({ customerId }) => {
  const limit = 10;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const count = useSelector(selectCustomerTicketLength);
  const pagesCount = useMemo(() => Math.ceil(count / limit), [count]);
  const data = useSelector(selectCustomerTickets);
  const loading = useSelector(selectCustomerTicketLoading);
  const [fieldData, setFieldData] = useState({
    status: [],
    createdFrom: null,
    createdTo: null,
    offset: 0,
  });

  const currentPage = useMemo(() => {
    return Math.floor((fieldData.offset || 0) / limit) + 1;
  }, [fieldData.offset, fieldData.limit]);

  const columns = [
    {
      title: 'Ticket ID',
      dataIndex: 'id',
      key: 'id',
      render: (_, data) => (
        <ClickableTicketId
          onClick={() => navigate(`/project-management/ticket/${data?.uuid}`)}
        >
          <p>{data?.id}</p>
        </ClickableTicketId>
      ),
    },
    {
      title: 'title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, data) => {
        return <Tag type="ticketStatuses" variant={data?.status} widthParent={110} /> || '-';
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => <>{<Tag type="ticketPriority" variant={priority} /> || '-'}</>,
    },
    {
      title: 'Creator',
      dataIndex: 'creator',
      key: 'creator',
      render: (_, data) => (
        <div>
          <p>{data?.createdBy?.name}</p>
        </div>
      ),
    },
    {
      title: 'Assignee',
      dataIndex: 'assignedUsers',
      key: 'assignedUsers',
      render: (assignees = [], _, index) => (
        <AvatarGroup data={assignees} maxVisible={4} index={index} />
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_, data) => (
        <CreatedDateBadge>
          <p>{formatDateTime(data?.createdAt)}</p>
        </CreatedDateBadge>
      ),
    },
    {
      title: 'Updated Date',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (_, data) => (
        <UpdatedDateBadge>
          <p>{formatDateTime(data?.updatedAt)}</p>
        </UpdatedDateBadge>
      ),
    },
  ];

  const mobileColumns = [
    {
      title: 'Ticket ID',
      dataIndex: 'id',
      key: 'id',
      render: (_, data) => (
        <ClickableTicketId
          onClick={() => navigate(`/project-management/ticket/${data?.uuid}`)}
        >
          <p>{data?.id}</p>
        </ClickableTicketId>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, data) => {
        return <Tag type="ticketStatuses" variant={data?.status} widthParent={110} /> || '-';
      },
    },
  ];

  const handleSearchChange = (field, value) => {
    setFieldData((prev) => ({
      ...prev,
      [field]: value,
      offset: 0,
    }));
  };

  const handleDateChange = (field, value) => {
    setFieldData((prev) => ({
      ...prev,
      [field]: value,
      offset: 0,
    }));
  };

  const handleClearAll = () => {
    setFieldData({
      status: [],
      createdFrom: null,
      createdTo: null,
      offset: 0,
    });
  };

  const onPagonationChange = (page) => {
    setFieldData((prev) => ({
      ...prev,
      offset: (page - 1) * limit,
    }));
  };
  useEffect(() => {
    dispatch(
      getCustomersTicket({
        customerId: customerId,
        ...fieldData,
        limit: limit,
        offset: fieldData.offset,
        status: fieldData.status?.map((item) => item.value) || [],
        createdFrom: formatDateTime(fieldData.createdFrom, true),
        createdTo: formatDateTime(fieldData.createdTo, true),
      })
    );
  }, [customerId, fieldData]);

  const renderExpandableContent = (row) => {
    return (
      <ExpandableWrapper>
        <Row>
          <ExpandedLabel>Priority</ExpandedLabel>
          <ExpandedValue>
            {row?.priority ? <Tag type="ticketPriority" variant={row?.priority} /> : '-'}
          </ExpandedValue>
        </Row>
        <Row>
          <ExpandedLabel>Creator</ExpandedLabel>
          <ExpandedValue>{row?.createdBy?.name || '-'}</ExpandedValue>
        </Row>
        <Row>
          <ExpandedLabel>Assignee</ExpandedLabel>
          <ExpandedValue>{row?.assignee || '-'}</ExpandedValue>
        </Row>
        <Row>
          <ExpandedLabel>Created Date</ExpandedLabel>
          <ExpandedValue>
            <CreatedDateBadge>
              <p>{formatDateTime(row?.createdAt)}</p>
            </CreatedDateBadge>
          </ExpandedValue>
        </Row>
        <Row>
          <ExpandedLabel>Updated Date</ExpandedLabel>
          <ExpandedValue>
            <UpdatedDateBadge>
              <p>{formatDateTime(row?.updatedAt)}</p>
            </UpdatedDateBadge>
          </ExpandedValue>
        </Row>
      </ExpandableWrapper>
    );
  };

  return (
    <>
      <FiltersWrapper>
        <FiltersWrapperDiv>
          <AsyncSelect
            showItemsContainer={false}
            placeholder="Status"
            isClearable
            isSearchable={false}
            isMulti
            hideSelectedOptions={false}
            value={fieldData.status}
            defaultOptions={TICKET_STATUS_OPTIONS}
            loadOptions={(inputValue, callback) => {
              callback(TICKET_STATUS_OPTIONS);
            }}
            onChange={(value) => handleSearchChange('status', value)}
          />

          <CustomDatePickerWrapper>
            <CustomDatePicker
              value={fieldData.createdFrom}
              onChange={(date) => handleDateChange('createdFrom', date)}
              placeholder="Created from"
              disableFuture
              maxDate={fieldData.createdTo ? dayjs(fieldData.createdTo) : null}
            />
          </CustomDatePickerWrapper>

          <CustomDatePickerWrapper>
            <CustomDatePicker
              value={fieldData.createdTo}
              onChange={(date) => handleDateChange('createdTo', date)}
              placeholder="Created to"
              disableFuture
              minDate={fieldData.createdFrom ? dayjs(fieldData.createdFrom) : null}
            />
          </CustomDatePickerWrapper>
        </FiltersWrapperDiv>
        <Line>
          <ClearAllText onClick={handleClearAll}>Clear all</ClearAllText>
        </Line>
      </FiltersWrapper>

      <Table
        loading={loading}
        columns={columns}
        data={data}
        onPaginationChange={onPagonationChange}
        totalPages={pagesCount}
        currentPage={currentPage}
      />
      <MobileList columns={mobileColumns} data={data} expandable={renderExpandableContent} />
    </>
  );
};

export default TicketHistory;
